import { Injectable } from '@angular/core';
import { Order, OrderHeader, OrderItem, OrderStatus } from '@usf/ngrx-order';
import { Product } from '@shared/models/product.model';
import { DownloadFileTypes } from '@shared/constants/order-download-file';
import { Customer } from '@usf/customer-types';
import { PlatformService } from '@panamax/app-state';
import { DateService } from '../date.service';
import jsPDF from 'jspdf';
import autoTable, { CellDef, CellInput, RowInput } from 'jspdf-autotable';
import 'jspdf-autotable';
import { timezoneByCode } from '@shared/constants/timezone-constants';
import { OrderStatusDetails } from '@shared/constants/order-status-title.enum';
import { CurrencyService } from '../currency.service';
import { DatePipe, formatCurrency } from '@angular/common';
import { OrderMinimumViewModel } from '@order/models/order-minimum-view-model';
import { SubmittedOrderTallies } from '@order/models/submitted-order-view-model';
import { TranslateService } from '@ngx-translate/core';
import { Shipment, ShipmentTypeEnum } from '@order/models/order-view-model';
import { FileHandlerBaseService } from '../file-handler/file-handler-base.service';
import { ToastService } from '../toast/toast.service';
import { Platform } from '@ionic/angular';
import { SentenceCasePipe } from '@shared/pipes/sentence-case.pipe';
import { LoadingSpinnerService } from '../loading-spinner/loading-spinner.service';
import { Capacitor } from '@capacitor/core';
import {
  getProntoCutoffDayIndexes,
  isProntoDay,
  isProntoEnabled,
} from '@shared/helpers/pronto.helpers';

@Injectable({
  providedIn: 'root',
})
export class OrderPrintService extends FileHandlerBaseService {
  grey = '#767676';

  constructor(
    readonly platformService: PlatformService,
    toastService: ToastService,
    public translateService: TranslateService,
    public platform: Platform,
    public datePipe: DatePipe,
    public sentenceCasePipe: SentenceCasePipe,
    public loadingSpinnerService: LoadingSpinnerService,
  ) {
    super(
      toastService,
      translateService,
      platform,
      datePipe,
      sentenceCasePipe,
      loadingSpinnerService,
    );
  }

  async printSubmittedOrder(
    orders: Order[],
    products: Map<number, Product>,
    customer: Customer,
    filePrefix: string,
    tallies: SubmittedOrderTallies,
    orderMinimum?: OrderMinimumViewModel,
    shipToCustomer?: Customer,
    shipToDepartmentName?: string,
    orderStatusDetails?: OrderStatusDetails,
  ) {
    const fileName = `${filePrefix}_Cust${
      orders[0]?.orderHeader?.customerNumber
    }_${DateService.formatDateMaintainUTC(
      orders[0]?.orderHeader?.confirmedDeliveryDate,
      '',
    )}`;

    const blob = this.formatSubmittedOrderToPDFBlob(
      orders,
      products,
      customer,
      fileName,
      filePrefix,
      tallies,
      orderMinimum,
      shipToCustomer,
      shipToDepartmentName,
      orderStatusDetails,
    );

    return await this.printFile(
      blob,
      fileName,
      DownloadFileTypes.PDF.contentType,
    );
  }

  async printCart(
    orderHeader: OrderHeader,
    shipments: Shipment[],
    customer: Customer,
    orderMinimum?: OrderMinimumViewModel,
    prontoDeliveryDays?: number[],
  ) {
    const deliveryDate = !!orderHeader?.confirmedDeliveryDate
      ? DateService.formatShortDateMaintainUTC(
          orderHeader?.confirmedDeliveryDate,
          '',
        )
      : 'N/A';

    const fileName = `Review_Order_${orderHeader?.customerNumber}_${deliveryDate}`;

    const blob = this.formatCartToPDFBlob(
      orderHeader,
      shipments,
      customer,
      orderMinimum,
      fileName,
      prontoDeliveryDays,
    );

    return await this.printFile(
      blob,
      fileName,
      DownloadFileTypes.PDF.contentType,
    );
  }

  async printFile(
    fileData: Blob | string,
    fileName: string,
    contentType: string,
    openWithDialog: boolean = true,
  ) {
    if (Capacitor.isNativePlatform()) {
      const file = await this.saveFile(fileData, fileName, contentType);
      return await this.openFileNative(file, contentType, openWithDialog);
    } else {
      return this.printWeb(fileData, contentType);
    }
  }

  formatSubmittedOrderToPDFBlob(
    orders: Order[],
    products: Map<number, Product>,
    customer: Customer,
    fileName: string,
    pageName: string,
    tallies: SubmittedOrderTallies,
    orderMinimum?: OrderMinimumViewModel,
    shipToCustomer?: Customer,
    shipToDepartmentName?: string,
    orderStatusDetails?: OrderStatusDetails,
  ) {
    const doc = new jsPDF({ orientation: 'l', unit: 'px' });
    doc.setProperties({ title: fileName });

    const orderConfirmation = pageName === 'Order_Confirmation';

    orders.forEach((order, index) => {
      let orderHeader = order?.orderHeader;
      let orderItems = order?.orderItems;

      if (!orderConfirmation && !!orderMinimum) {
        orderHeader = {
          ...orderHeader,
          minOrderCases: orderMinimum.customerMinOrderCases,
          minOrderCasesDiff: orderMinimum.remainingCases,
          minOrderDollars: orderMinimum.customerMinOrderDollars,
          minOrderDollarsDiff: orderMinimum.remainingDollars,
        };
      }
      const startingPageNumber = doc.getCurrentPageInfo().pageNumber;

      const orderItemTableColumns: CellDef[][] = [
        [
          {
            content: 'Product Description',
            styles: {
              fillColor: this.grey,
              overflow: 'linebreak',
            },
          },
          { content: 'Brand', styles: { fillColor: this.grey } },
          {
            content: 'Pack Size',
            styles: { fillColor: this.grey, cellWidth: 40 },
          },
          { content: 'Price', styles: { fillColor: this.grey, cellWidth: 50 } },
          { content: 'Ordered CS/EA', styles: { fillColor: this.grey } },
          { content: 'Reserved CS/EA', styles: { fillColor: this.grey } },
          { content: 'Extended Price', styles: { fillColor: this.grey } },
        ],
      ];

      const { orderItemRows, unitsReserved, eachesReserved } =
        this.createSubmittedOrderItemRows(orderItems, products, customer);

      const orderHeaderTableRows = this.createOrderHeaderRows(
        orderHeader,
        orderItems,
        customer,
        unitsReserved,
        eachesReserved,
        false,
        orderMinimum,
        tallies,
        null,
        shipToCustomer,
        shipToDepartmentName,
        orderStatusDetails,
      );

      const adjustedHeight =
        (orderHeaderTableRows.length -
          6 +
          (orderHeader?.specialDeliveryInstructions.length / 25 || 0) +
          (!!shipToCustomer ? 2 : 0)) *
        10;

      autoTable(doc, {
        body: orderHeaderTableRows,
        startY: 45,
        margin: {
          left: 50,
        },
        theme: 'plain',
        tableWidth: 'wrap',
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 120 },
          2: { cellWidth: 100 },
          3: { cellWidth: 80 },
          4: { cellWidth: 80 },
          5: { cellWidth: 40 },
        },
        styles: {},
      });

      //Order Items
      autoTable(doc, {
        head: orderItemTableColumns,
        body: orderItemRows,
        startY: 180 + adjustedHeight,
        theme: 'grid',
        tableWidth: 'auto',
        margin: {
          top: 50,
        },
        columnStyles: {
          0: { halign: 'left' },
        },
        styles: {
          halign: 'center',
          fontSize: 8,
        },
      });

      doc.setFontSize(8);
      const finalY: number = (doc as any).previousAutoTable.finalY; // must be any due to a lack of types from jspdfautotable

      if (finalY > doc.internal.pageSize.getHeight() - 30) {
        //Breakpoint for hitting the footer
        doc.text(
          'The prices shown above are estimates only. Actual prices will be reflected on your invoice. Thank you for your order!',
          100,
          doc.internal.pageSize.getHeight() - 30,
        );
      } else {
        doc.text(
          'The prices shown above are estimates only. Actual prices will be reflected on your invoice. Thank you for your order!',
          30,
          finalY + 10,
        );
      }

      const title = orderConfirmation ? 'Order Confirmation' : 'Placed Order';
      const subtitle = `Order Number: ${orderHeader?.tandemOrderNumber}`;
      this.createHeader(doc, startingPageNumber, title, subtitle);

      if (index + 1 < orders.length) {
        doc.addPage();
      }
    });

    this.createFooter(doc);
    return doc.output('blob');
  }

  formatCartToPDFBlob(
    orderHeader: OrderHeader,
    shipments: Shipment[],
    customer: Customer,
    orderMinimum: OrderMinimumViewModel,
    fileName: string,
    prontoDeliveryDays?: number[],
  ): Blob {
    const doc = new jsPDF({ orientation: 'l', unit: 'px' });
    doc.setProperties({ title: fileName });

    if (!!orderMinimum) {
      orderHeader = {
        ...orderHeader,
        minOrderCases: orderMinimum.customerMinOrderCases,
        minOrderCasesDiff: orderMinimum.remainingCases,
        minOrderDollars: orderMinimum.customerMinOrderDollars,
        minOrderDollarsDiff: orderMinimum.remainingDollars,
      };
    }

    const orderItemTableColumns: CellDef[][] = [
      [
        {
          content: 'Product Description',
          styles: {
            fillColor: this.grey,
            overflow: 'linebreak',
          },
        },
        { content: 'Brand', styles: { fillColor: this.grey } },
        {
          content: 'Pack Size',
          styles: { fillColor: this.grey, cellWidth: 40 },
        },
        { content: 'Price', styles: { fillColor: this.grey, cellWidth: 50 } },
        { content: 'Ordered CS/EA', styles: { fillColor: this.grey } },
      ],
    ];

    shipments.forEach((shipment, index) => {
      const startingPageNumber = doc.getCurrentPageInfo().pageNumber;

      const orderItemRows = this.createIPOrderItemRows(shipment, customer);

      const orderHeaderTableRows = this.createOrderHeaderRows(
        orderHeader,
        shipment?.cartItems?.map(cartItem => cartItem.orderItem),
        customer,
        0,
        0,
        true,
        orderMinimum,
        shipment.shipmentTallies,
        shipment.shipmentType,
      );

      autoTable(doc, {
        body: orderHeaderTableRows,
        startY: 45,
        margin: {
          left: 50,
        },
        theme: 'plain',
        tableWidth: 'wrap',
        columnStyles: {
          0: { cellWidth: 100 },
          1: { cellWidth: 120 },
          2: { cellWidth: 100 },
          3: { cellWidth: 80 },
          4: { cellWidth: 80 },
          5: { cellWidth: 40 },
        },
        styles: {},
      });

      const adjustedHeight =
        (orderHeaderTableRows?.length -
          6 +
          (orderHeader?.specialDeliveryInstructions?.length / 25 || 0)) *
        10;

      //Order Items
      autoTable(doc, {
        head: orderItemTableColumns,
        body: orderItemRows,
        startY: 180 + adjustedHeight,
        theme: 'grid',
        tableWidth: 'auto',
        margin: {
          top: 50,
        },
        columnStyles: {
          0: { halign: 'left' },
        },
        styles: {
          halign: 'center',
          fontSize: 8,
        },
      });

      doc.setFontSize(8);
      const finalY: number = (doc as any).previousAutoTable.finalY; // must be any due to a lack of types from jspdfautotable

      if (finalY > doc.internal.pageSize.getHeight() - 30) {
        //Breakpoint for hitting the footer
        doc.text(
          'The prices shown above are estimates only. Actual prices will be reflected on your invoice. Thank you for your order!',
          100,
          doc.internal.pageSize.getHeight() - 30,
        );
      } else {
        doc.text(
          'The prices shown above are estimates only. Actual prices will be reflected on your invoice. Thank you for your order!',
          30,
          finalY + 10,
        );
      }

      const isProntoShipment =
        (orderHeader?.orderStatus === OrderStatus.IN_PROGRESS ||
          orderHeader?.orderStatus === OrderStatus.SUBMITTED ||
          orderHeader?.orderStatus === OrderStatus.SUBMITTED_CREDIT_HOLD ||
          orderHeader?.orderStatus === OrderStatus.SUBMITTED_WITH_EXCEPTIONS) &&
        !!prontoDeliveryDays &&
        isProntoDay(prontoDeliveryDays, shipment?.deliveryDate?.toString());

      const title = `${
        shipment?.shipmentType === ShipmentTypeEnum.vendor
          ? 'Direct Ship Order'
          : isProntoShipment
            ? 'Pronto Order'
            : 'US Foods Order'
      }, Expected by ${
        !!shipment?.deliveryDate
          ? DateService.formatShortDateMaintainUTC(shipment?.deliveryDate)
          : 'N/A'
      }`;
      const subtitle = `PO# : ${
        !!orderHeader?.purchaseOrderNumber &&
        orderHeader?.purchaseOrderNumber?.length !== 0
          ? orderHeader.purchaseOrderNumber
          : '--'
      }`;
      this.createHeader(doc, startingPageNumber, title, subtitle);

      if (index + 1 < shipments?.length) {
        doc.addPage();
      }
    });
    this.createFooter(doc);
    return doc.output('blob');
  }

  createHeader(
    doc: jsPDF,
    startingPageNumber: number,
    title: string,
    subtitle: string,
  ) {
    const endingPageNumber = doc.getCurrentPageInfo().pageNumber;

    for (let i = startingPageNumber; i <= endingPageNumber; i++) {
      doc.setPage(i);
      const pageSize = doc.internal.pageSize;
      const pageHeight = pageSize.height
        ? pageSize.height
        : pageSize.getHeight();

      const image = new Image();
      image.src = '../assets/images/usfoods-logo-print.jpg';
      doc.addImage(image.src, 10, 10, 27, 25);

      doc.setFontSize(10);
      doc.text(subtitle, 45, 30);
      doc.setFontSize(16);
      doc.text(title, 45, 20);
      doc.setFontSize(10);

      doc.setDrawColor(this.grey);
      doc.line(10, 40, pageSize.getWidth() - 10, 40);
    }
    return doc;
  }

  createFooter(doc: jsPDF) {
    const pageCount: number = (doc as any).internal.getNumberOfPages();
    for (var i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageSize = doc.internal.pageSize;
      const pageHeight = pageSize.height
        ? pageSize.height
        : pageSize.getHeight();

      doc.line(10, pageHeight - 30, pageSize.getWidth() - 10, pageHeight - 30);

      doc.text(
        'For the exclusive use by US Foods customers',
        20,
        pageHeight - 16,
      );

      const printDate = new Date();
      doc.text(
        `${printDate.toLocaleDateString('en-us', {
          weekday: 'long',
        })} ${printDate
          .toLocaleDateString('en-US')
          .toString()}  |  Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - 130,
        pageHeight - 16,
      );
    }
    return doc;
  }

  createIPOrderItemRows(shipment: Shipment, customer: Customer) {
    const orderItemRows = shipment?.cartItems?.map(item => {
      const product = {
        summary: item.productState,
        pricing: item.pricing,
        productNumber: item.productState?.productNumber,
      };

      const showPrice = customer?.ogPrintPriceInd !== 'N';

      const price = showPrice
        ? product?.summary?.breakable
          ? `${CurrencyService.toUSDString(product?.pricing?.unitPrice)} / ${
              product?.summary?.priceUom || 'N/A'
            }\n${CurrencyService.toUSDString(product?.pricing?.eachPrice)} / ${
              (product?.summary?.catchWeightFlag
                ? product?.summary.priceUom
                : product?.summary?.eachUom) || 'N/A'
            }`
          : `${CurrencyService.toUSDString(product?.pricing?.unitPrice)} / ${
              product?.summary?.priceUom || 'N/A'
            }`
        : 'No Price';

      return [
        `${product?.summary?.productDescTxtl || 'PRODUCT NOT FOUND'} (${
          product?.productNumber
        })`,
        product?.summary?.brand,
        product?.summary?.salesPackSize,
        price,
        `${
          item?.orderItem?.unitsOrdered?.currentValue
            ? item.orderItem?.unitsOrdered.currentValue.toString()
            : '0'
        }/${
          item?.orderItem?.eachesOrdered?.currentValue
            ? item?.orderItem?.eachesOrdered?.currentValue.toString()
            : '0'
        }`,
      ];
    });
    return orderItemRows;
  }

  createSubmittedOrderItemRows(
    orderItems: OrderItem[],
    products: Map<number, Product>,
    customer: Customer,
  ) {
    let eachesReserved = 0;
    let unitsReserved = 0;
    const orderItemRows = orderItems.map(item => {
      const product = products.get(item.productNumber);

      eachesReserved += item.eachesReserved;
      unitsReserved += item.unitsReserved;

      const showPrice = customer?.ogPrintPriceInd !== 'N';

      const price = showPrice
        ? product?.summary?.breakable
          ? `${CurrencyService.toUSDString(item.unitPrice)} / ${
              product?.summary?.priceUom || 'N/A'
            }\n${CurrencyService.toUSDString(item.eachPrice)} / ${
              (product?.summary?.catchWeightFlag
                ? product?.summary.priceUom
                : product?.summary?.eachUom) || 'N/A'
            }`
          : `${CurrencyService.toUSDString(item.unitPrice)} / ${
              product?.summary?.priceUom || 'N/A'
            }`
        : 'No Price';

      return [
        `${product?.summary?.productDescTxtl || 'PRODUCT NOT FOUND'} (${
          product?.productNumber
        })`,
        product?.summary?.brand,
        product?.summary?.salesPackSize,
        price,
        `${
          item.unitsOrdered?.currentValue
            ? item.unitsOrdered.currentValue.toString()
            : '0'
        }/${
          item.eachesOrdered?.currentValue
            ? item.eachesOrdered?.currentValue.toString()
            : '0'
        }`,
        `${item.unitsReserved}/${item.eachesReserved}`,
        showPrice ? CurrencyService.toUSDString(item.totalDollars) : 'No Price',
      ];
    });
    return { orderItemRows, unitsReserved, eachesReserved };
  }

  createOrderHeaderRows(
    orderHeader: OrderHeader,
    orderItems: OrderItem[],
    customer: Customer,
    unitsReserved: number,
    eachesReserved: number,
    isIPOrder: boolean,
    orderMinimum?: OrderMinimumViewModel,
    tallies?: SubmittedOrderTallies,
    shipmentType?: ShipmentTypeEnum,
    shipToCustomer?: Customer,
    shipToDepartmentName?: string,
    orderStatusDetails?: OrderStatusDetails,
  ) {
    const timeZone =
      timezoneByCode.get(customer?.timeZone)?.location || 'America/Chicago';

    const hasETADetails =
      !!orderHeader.etaRangeMinimum && !!orderHeader.etaRangeMaximum;

    const deliveryTime = orderHeader.orderDelivered
      ? new Date(orderHeader.deliveredDtm).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone,
          timeZoneName: 'short',
        })
      : `${new Date(orderHeader.etaRangeMinimum).toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          timeZone,
        })} - ${new Date(orderHeader.etaRangeMaximum).toLocaleTimeString(
          'en-US',
          {
            hour: '2-digit',
            minute: '2-digit',
            timeZone,
            timeZoneName: 'short',
          },
        )}`;

    const orderStatus = orderStatusDetails
      ? orderStatusDetails
      : OrderStatusDetails[orderHeader?.orderStatus];

    const department = customer?.departments?.find(
      dept =>
        dept.departmentNumber === orderHeader?.departmentNumber?.toString(),
    );
    const isSubmitted =
      orderHeader.orderStatus === OrderStatus.SUBMITTED ||
      orderHeader.orderStatus === OrderStatus.SUBMITTED_CREDIT_HOLD ||
      orderHeader.orderStatus === OrderStatus.SUBMITTED_WITH_EXCEPTIONS;
    const prontoDeliveryDays = this.getProntoDeliveryDays(customer);
    const utcDay = isSubmitted
      ? new Date(orderHeader?.confirmedDeliveryDate).getUTCDay()
      : new Date(orderHeader?.requestedDeliveryDate).getUTCDay();
    const isProntoOrderAndDay =
      (orderHeader?.orderStatus === OrderStatus.IN_PROGRESS || isSubmitted) &&
      !!prontoDeliveryDays
        ? prontoDeliveryDays.includes(utcDay)
        : false;

    const shouldShowMinimumOrder =
      orderHeader?.orderType === 'RT' &&
      (customer?.minOrderDollars > 0 || customer?.minOrderCases > 0) &&
      shipmentType !== ShipmentTypeEnum.vendor &&
      !isProntoOrderAndDay;

    const isMinimumOrderMet =
      (orderHeader?.minOrderCasesDiff <= 0 &&
        customer?.minOrderCases > 0 &&
        orderHeader?.minOrderDollarsDiff <= 0 &&
        customer?.minOrderDollars > 0) ||
      orderMinimum?.isMet;

    const specialInstructionsTitle =
      orderHeader.orderType === 'VS'
        ? 'Special Delivery Instructions:'
        : 'Special Instructions:';

    const shippingCustomer = !!shipToCustomer ? shipToCustomer : customer;

    const address = `${shippingCustomer?.address1} ${
      shippingCustomer?.address2 ? shippingCustomer?.address2 : ''
    }\n${shippingCustomer?.city}, ${shippingCustomer?.state} ${
      shippingCustomer?.zip
    }`;

    const shipToDepartment = !!orderHeader?.shipToDepartment
      ? `(${shipToDepartmentName} #${orderHeader?.shipToDepartment})\n`
      : '';

    const shipTo = !!shipToCustomer
      ? `${shippingCustomer?.customerName} #${shippingCustomer?.customerNumber}\n${shipToDepartment}` +
        address
      : address;

    const orderHeaderTableRows: CellInput[][] = [
      [
        {
          content: isIPOrder ? 'Customer PO#: ' : 'Order Number: ',
          styles: { fontStyle: 'bold' },
        },
        isIPOrder
          ? orderHeader?.purchaseOrderNumber?.length !== 0
            ? orderHeader.purchaseOrderNumber
            : '--'
          : orderHeader?.tandemOrderNumber?.toString(),
        {
          content: 'Status: ',
          styles: { fontStyle: 'bold', overflow: 'visible' },
        },
        {
          content:
            shouldShowMinimumOrder && isMinimumOrderMet
              ? `${this.translateService.instant(
                  orderStatus?.title,
                )} - Minimum Order Met`
              : this.translateService.instant(orderStatus?.title),
          styles: { overflow: 'visible' },
        },
        '',
        '',
      ],
      [
        {
          content: !isIPOrder ? 'Customer PO#: ' : '',
          styles: { fontStyle: 'bold' },
        },
        !isIPOrder
          ? orderHeader?.purchaseOrderNumber?.length !== 0
            ? orderHeader.purchaseOrderNumber
            : '--'
          : '',
        { content: 'Est. Delivery: ', styles: { fontStyle: 'bold' } },
        !!orderHeader.deliveryEta
          ? new Date(orderHeader.deliveryEta).toLocaleDateString('en-US', {
              timeZone,
            })
          : !!orderHeader.confirmedDeliveryDate
            ? new Date(orderHeader.confirmedDeliveryDate).toLocaleDateString(
                'en-US',
                { timeZone: 'UTC' },
              )
            : 'N/A',
        '',
        '',
      ],
      [
        { content: 'Customer: ', styles: { fontStyle: 'bold' } },
        `${customer?.customerName} (${customer?.customerNumber})`,
        {
          content: hasETADetails
            ? orderHeader.orderDelivered
              ? 'Delivery Time'
              : 'Est. Delivery Time: '
            : '',
          styles: { fontStyle: 'bold' },
        },
        {
          content: hasETADetails ? deliveryTime : '',
          styles: { overflow: 'visible' },
        },
      ],
      [
        {
          content: department ? 'Department: ' : '',
          styles: { fontStyle: 'bold' },
        },
        department
          ? `${department.departmentName} (${department.departmentNumber})`
          : '',
        {
          content: 'Cases Ordered: ',
          styles: { fontStyle: 'bold' },
        },
        {
          content:
            tallies?.totalCasesOrdered?.toString() ??
            orderHeader?.totalUnits?.toString(),
        },
        {
          content: !!tallies?.totalCasesReserved ? 'Cases Reserved: ' : '',
          styles: { fontStyle: 'bold' },
        },
        !!tallies?.totalCasesReserved
          ? tallies?.totalCasesReserved.toString()
          : '',
      ],
      [
        { content: 'Ship To: ', styles: { fontStyle: 'bold' } },
        shipTo,
        { content: 'Eaches Ordered: ', styles: { fontStyle: 'bold' } },
        tallies?.totalEachesOrdered?.toString() ??
          orderHeader.totalEaches.toString(),
        {
          content: !!tallies?.totalEachesReserved ? 'Eaches Reserved: ' : '',
          styles: { fontStyle: 'bold' },
        },
        !!tallies?.totalEachesReserved
          ? tallies?.totalEachesReserved.toString()
          : '',
      ],
      [
        { content: specialInstructionsTitle, styles: { fontStyle: 'bold' } },
        orderHeader.specialDeliveryInstructions || 'None',
        { content: 'Total Line Items: ', styles: { fontStyle: 'bold' } },
        orderItems?.length,
        {
          content: 'Est. Order Total: ',
          styles: { fontStyle: 'bold', overflow: 'visible' },
        },
        {
          content:
            customer?.ogPrintPriceInd !== 'N'
              ? isIPOrder
                ? formatCurrency(
                    tallies?.totalPrice,
                    'en-US',
                    '$',
                    'USD',
                    '1.2-2',
                  )
                : formatCurrency(
                    (orderHeader.totalDollars || 0) +
                      (orderHeader.totalSalesTax || 0) +
                      (orderHeader.totalCharges || 0) -
                      Math.abs(orderHeader.totalAllowances || 0) || 0,
                    'en-US',
                    '$',
                    'USD',
                    '1.2-2',
                  )
              : 'No Price',
          styles: { overflow: 'visible' },
        },
      ],
      [
        '',
        '',
        {
          content: shouldShowMinimumOrder ? 'Minimum Order*: ' : '',
          styles: { fontStyle: 'bold' },
        },
        shouldShowMinimumOrder
          ? `${
              customer?.minOrderDollars > 0
                ? CurrencyService.toUSDString(customer?.minOrderDollars)
                : '--'
            } / ${
              customer?.minOrderCases > 0
                ? `${customer?.minOrderCases} CS`
                : '--'
            }`
          : '',
        {
          content:
            shouldShowMinimumOrder && isMinimumOrderMet
              ? `Minimum Order Met`
              : '',
          styles: { fontStyle: 'bold' },
        },
      ],
      [
        '',
        '',
        {
          content:
            shouldShowMinimumOrder && !isMinimumOrderMet ? `Difference: ` : '',
          styles: { fontStyle: 'bold' },
        },
        shouldShowMinimumOrder && !isMinimumOrderMet
          ? `${
              orderHeader?.minOrderDollarsDiff > 0
                ? CurrencyService.toUSDString(orderHeader?.minOrderDollarsDiff)
                : '--'
            } / ${
              orderHeader?.minOrderCasesDiff > 0
                ? `${orderHeader?.minOrderCasesDiff} CS`
                : `--`
            }`
          : '',
      ],
      [
        '',
        '',
        {
          content: shouldShowMinimumOrder
            ? '*Minimum order calculation accounts for multiple orders delivered the same day'
            : '',
          styles: { fontSize: 8, overflow: 'visible' },
        },
      ],
    ];

    if (!shouldShowMinimumOrder) {
      orderHeaderTableRows.splice(-2, 2); //Removes empty string arrays
    }

    return orderHeaderTableRows;
  }

  getProntoDeliveryDays(customer: Customer): Number[] {
    return isProntoEnabled(customer)
      ? getProntoCutoffDayIndexes(customer)
      : undefined;
  }
}
